import { Link } from "react-router-dom";
import {
  Sparkles,
  ArrowRight,
  Eye,
  TrendingUp,
  MousePointerClick,
  MessageSquare,
  Activity,
} from "lucide-react";
import SignalPreview from "./components/SignalPreview";

export default function Home() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-primary-950 text-background-50">
      {/* Arka plan ışıkları */}
      <div className="pointer-events-none absolute -top-40 -right-40 h-[620px] w-[620px] rounded-full bg-accent-500/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-48 -left-40 h-[540px] w-[540px] rounded-full bg-primary-500/30 blur-3xl" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />

      <Header />
      <main className="relative z-10">
        <Hero />
        <ValueCards />
        <HowItWorks />
        <FinalCta />
      </main>
      <Footer />
    </div>
  );
}

function Header() {
  return (
    <header className="relative z-20 mx-auto flex max-w-6xl items-center justify-between px-4 py-6 md:px-6">
      <Link to="/" className="flex cursor-pointer items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-500 text-primary-950">
          <Sparkles className="h-5 w-5" strokeWidth={2.2} />
        </span>
        <span className="font-heading text-base font-bold tracking-tight">SMART DEMO</span>
      </Link>

      <Link
        to="/demo-platformu"
        className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition hover:bg-accent-400"
      >
        Demoyu Dene
        <ArrowRight className="h-4 w-4" />
      </Link>
    </header>
  );
}

function Hero() {
  return (
    <section className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-4 py-14 md:px-6 lg:grid-cols-2 lg:py-24">
      {/* Sol: copy */}
      <div className="animate-fade-in-up">
        <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-accent-400/30 bg-accent-500/10 px-4 py-1.5 text-xs font-semibold text-accent-300">
          <Sparkles className="h-3.5 w-3.5" />
          Akıllı Müşteri Deneyimi
        </span>

        <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.12] tracking-tight md:text-5xl lg:text-[3.4rem]">
          Müşteriniz Sadece Mesaj Yazmaz.
          <span className="block text-accent-300">Davranışı da Sinyal Verir.</span>
        </h1>

        <p className="mt-6 max-w-xl text-base leading-relaxed text-background-300 md:text-lg">
          Smart Demo ile müşterinizin hangi hizmetleri incelediğini, hangi içeriklerle
          ilgilendiğini ve satın alma niyetinin nasıl güçlendiğini deneyimleyin.
        </p>

        <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <Link
            to="/demo-platformu"
            className="group inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-7 py-4 text-base font-bold text-primary-950 transition hover:bg-accent-400"
          >
            Demoyu Hemen Dene
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
          <span className="text-sm font-medium text-background-400">
            Ücretsiz • Bilgi girmeden de deneyebilirsiniz
          </span>
        </div>
      </div>

      {/* Sağ: Signal preview */}
      <div className="animate-fade-in-up lg:pl-4">
        <SignalPreview />
      </div>
    </section>
  );
}

function ValueCards() {
  const cards = [
    {
      icon: Eye,
      title: "Davranışı Gör",
      desc: "Müşterinin hangi hizmet, görsel ve videolarla ilgilendiğini anlayın.",
    },
    {
      icon: TrendingUp,
      title: "Potansiyeli Fark Et",
      desc: "Etkileşimler ilerledikçe müşterinin niyetinin nasıl güçlendiğini görün.",
    },
    {
      icon: MousePointerClick,
      title: "Doğru Anda Aksiyon Al",
      desc: "Yüksek ilgi gösteren müşterileri iletişim ve satış adımına taşıyın.",
    },
  ];

  return (
    <section className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-20">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.title}
              className="rounded-2xl border border-background-700 bg-background-900/40 p-7 transition hover:border-accent-400/40"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent-500/15 text-accent-300">
                <Icon className="h-6 w-6" strokeWidth={1.8} />
              </span>
              <h3 className="mt-5 font-heading text-lg font-bold text-background-50">
                {card.title}
              </h3>
              <p className="mt-2.5 text-sm leading-relaxed text-background-300">{card.desc}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      icon: MessageSquare,
      title: "Müşteri içerikleri inceler",
      desc: "Hizmet galerileri, görseller ve videolar üzerinden gezinir.",
    },
    {
      icon: Activity,
      title: "Davranış sinyalleri oluşur",
      desc: "Her etkileşim sistemde anlamlı bir sinyale dönüşür.",
    },
    {
      icon: TrendingUp,
      title: "Potansiyel ve niyet görünür hale gelir",
      desc: "Müşterinin ilgisi ölçülebilir bir potansiyele dönüşür.",
    },
  ];

  return (
    <section className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-20">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="font-heading text-2xl font-extrabold tracking-tight md:text-3xl">
          Nasıl Çalışır?
        </h2>
        <p className="mt-3 text-sm text-background-400 md:text-base">
          Üç basit adımda müşteri davranışını anlamlı hale getirin.
        </p>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
        {steps.map((step, index) => {
          const Icon = step.icon;
          return (
            <div key={step.title} className="relative rounded-2xl border border-background-700 bg-background-900/30 p-7">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-500 text-primary-950">
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </span>
                <span className="font-heading text-3xl font-extrabold text-background-700">
                  0{index + 1}
                </span>
              </div>
              <h3 className="mt-5 font-heading text-base font-bold text-background-50">
                {step.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-background-400">{step.desc}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-20">
      <div className="relative overflow-hidden rounded-3xl bg-accent-500 p-10 text-primary-950 md:p-14">
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-primary-950/10 blur-3xl" />
        <div className="relative mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-2xl font-extrabold leading-tight tracking-tight md:text-4xl">
            Smart Demo'yu Anlatmayalım.
            <span className="block">Size Yaşatalım.</span>
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-primary-900 md:text-base">
            Kendi firmanızla deneyin veya hazır bir sektör örneğini birkaç saniye içinde başlatın.
          </p>
          <Link
            to="/demo-platformu"
            className="group mt-8 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-primary-950 px-7 py-4 text-base font-bold text-background-50 transition hover:bg-primary-900"
          >
            Smart Demo'yu Başlat
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="relative z-10 mx-auto max-w-6xl px-4 pb-8 md:px-6">
      <div className="flex flex-col items-center justify-between gap-3 border-t border-background-800 pt-6 sm:flex-row">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-accent-500 text-primary-950">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.2} />
          </span>
          <span className="font-heading text-sm font-bold tracking-tight">SMART DEMO</span>
        </div>
        <p className="text-xs text-background-400">
          © {new Date().getFullYear()} Smart Demo. Tüm hakları saklıdır.
        </p>
      </div>
    </footer>
  );
}